import { find, findIndex, get, groupBy, sortBy } from 'lodash';

import {
  getRandomBoolean,
  getScorepointShuffleType,
  shuffleArray,
  getVisibleRowsAndColumns,
  enrichMatrixRows,
} from '../../../../../utils';
import { getGenericShuffleType } from '../../../../Answers/utils';
import { QUESTION_MATRIX_BIPOLAR_STYLES } from '../constants';

// This is the minimum width of each radio button with margins
// it is useful only for Horizontal styling
const answerWidth = 28;

function getPoleImage(questionAnswer, side, label) {
  return {
    imageUrl: get(questionAnswer, `image_url_${side}`, ''),
    optimizedImageUrl: get(questionAnswer, `optimized_image_url_${side}`, ''),
    label,
  };
}

export default function useSetup({ question }) {
  const { shuffle: shuffleType } = question;
  // shuffle the columns once,
  // only SHUFFLE_NONE and SHUFFLE_COLUMNS_REVERSE is allowed
  // we must apply the same shuffling for
  // columns, labels and rowLabels for each row
  const shouldReverseShuffle = getRandomBoolean();
  const columnsShuffleType = getScorepointShuffleType(shuffleType);
  const rowsShuffleType = getGenericShuffleType(question);

  const matrix = get(question, 'matrix', {});
  const matrixStyle = get(
    matrix,
    'openEndedConfig.style',
    QUESTION_MATRIX_BIPOLAR_STYLES.HORIZONTAL,
  );

  const answerColumnValueTypes = matrix.answerColumnValueTypes || [];
  const columnsGroupedByRows = groupBy(answerColumnValueTypes, 'answerId');

  let columns = matrix.columns || [];
  columns = sortBy(columns, ['order']);
  if (shouldReverseShuffle) {
    columns = shuffleArray(columns, columnsShuffleType, {
      forceReverse: shouldReverseShuffle,
    });
  }

  let labels = matrix.verticalLabels || [];
  labels = sortBy(labels, ['order']);
  if (shouldReverseShuffle) {
    labels = shuffleArray(labels, columnsShuffleType, {
      forceReverse: shouldReverseShuffle,
    });
  }

  let rows = enrichMatrixRows(question);
  rows = sortBy(rows, ['order']);
  rows = shuffleArray(rows, rowsShuffleType);
  ({ rows } = getVisibleRowsAndColumns(
    question,
    rows.map((row) => ({ ...row, refId: row.answerRefId })),
  ));

  const newRows = rows.map((answer) => {
    const { answerId, beforeLabel, afterLabel } = answer;
    const questionAnswer = find(question.answers, ['id', answerId]);
    const updatedRow = {};
    updatedRow.id = answerId;

    // Keep before/after labels and images as poles so reverse shuffle stays in sync
    let poles = [
      {
        label: beforeLabel,
        image: getPoleImage(questionAnswer, 'before', beforeLabel),
      },
      {
        label: afterLabel,
        image: getPoleImage(questionAnswer, 'after', afterLabel),
      },
    ];
    poles = shuffleArray(poles, columnsShuffleType, {
      forceReverse: shouldReverseShuffle,
    });

    updatedRow.rowLabels = {
      beforeLabel: poles[0].label,
      afterLabel: poles[1].label,
    };
    updatedRow.beforeImage = {
      ...poles[0].image,
      label: poles[0].label,
    };
    updatedRow.afterImage = {
      ...poles[1].image,
      label: poles[1].label,
    };

    updatedRow.columns = columns
      .filter(
        ({ id }) =>
          findIndex(columnsGroupedByRows[answerId], ['columnId', id]) !== -1,
      )
      .map(({ id }) => id);
    updatedRow.min = 0;
    updatedRow.max = updatedRow.columns.length - 1;
    updatedRow.initValue = (updatedRow.min + updatedRow.max) / 2;
    return updatedRow;
  });

  // Find the maximum length of columns between rows
  const columnsMaxLength = Math.max(
    ...newRows.map((answer) => answer.columns.length),
  );
  const minWidth = `${answerWidth * columnsMaxLength}px`;

  // TODO: Check how we can alleviate this side effect
  Pollfish.store.setShuffledAnswers(question.refId || question.id, newRows);

  return {
    rows: newRows,
    matrixStyle,
    minWidth,
    labels: labels.map(({ label }) => label),
    minAnswerWidth: answerWidth,
  };
}
